import { useEffect, useState } from "react";
import { Col, FormGroup, Input, Label, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import FixedSave from "../../../../components/Actions/FixedSave";
import { get, post } from "../../../../utils/helpers/api";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import CustomSelect from "../../../../components/Forms/CustomSelect";
import { useTranslation } from "react-i18next";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format, parseISO } from "date-fns";
import { useSettings } from "../../../../context/settings";
import WarehouseSelect from "../../../../components/Forms/WarehouseSelect";
import { useBranch } from "../../../../context/branch";

const REASONS = [
  { value: "Damaged goods", key: "reasonDamagedGoods" },
  { value: "Expired stock", key: "reasonExpiredStock" },
  { value: "Theft / loss", key: "reasonTheftLoss" },
  { value: "Supplier correction", key: "reasonSupplierCorrection" },
  { value: "Physical count correction", key: "reasonPhysicalCountCorrection" },
  { value: "Return to supplier", key: "reasonReturnToSupplier" },
  { value: "Other", key: "reasonOther" },
];

function CreateAdjustmentsPage() {
  const { t } = useTranslation();
  const { datePickerFormat } = useSettings();
  const { branchWarehouses } = useBranch();
  const navigate = useNavigate();
  const [saveLoading, setSaveLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  // Stock held in the chosen warehouse, which is what the adjustment corrects.
  const [warehouseStock, setWarehouseStock] = useState(null);
  const [form, setForm] = useState({
    product_id: "",
    warehouse_id: "",
    new_stock: "",
    reason: "",
    adjusted_at: new Date(),
    notes: "",
  });

  useEffect(() => {
    loadProducts();
  }, []);

  // Preselect the branch's default so the common case needs no extra click.
  useEffect(() => {
    if (!form.warehouse_id && branchWarehouses.length) {
      const preferred = branchWarehouses.find(w => w.is_default && w.type !== 'in_transit')
        ?? branchWarehouses.find(w => w.type !== 'in_transit');
      if (preferred) setForm(prev => ({ ...prev, warehouse_id: preferred.id }));
    }
  }, [branchWarehouses, form.warehouse_id]);

  // The product total is company-wide, so the level for this warehouse has to
  // be read separately or the difference shown would be meaningless.
  useEffect(() => {
    const loadLevel = async () => {
      if (!form.product_id || !form.warehouse_id) return setWarehouseStock(null);
      const data = await get(`/inventory/products/view/${form.product_id}`);
      const level = data?.stock_levels?.find(l => l.warehouse_id === form.warehouse_id);
      setWarehouseStock(level ? Number(level.quantity) : 0);
    };
    loadLevel();
  }, [form.product_id, form.warehouse_id]);

  const loadProducts = async () => {
    const data = await get("/inventory/products");
    if (data?.success) setProducts(data.products?.data ?? data.products ?? []);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (name === "product_id") {
      const product = products.find((p) => String(p.id) === value);
      setSelectedProduct(product ?? null);
    }
  };

  const submit = async () => {
    if (!form.product_id) return toast.error(t('inventory.selectProductRequired'));
    if (!form.warehouse_id) return toast.error(t('warehouses.selectWarehouseRequired'));
    if (form.new_stock === "") return toast.error(t('inventory.newStockRequired'));
    if (!form.reason) return toast.error(t('inventory.reasonRequired'));
    if (!form.adjusted_at) return toast.error(t('inventory.adjustmentDateRequired'));

    setSaveLoading(true);
    try {
      const data = await post("/inventory/stock-adjustments/store", {
        ...form,
        adjusted_at: form.adjusted_at ? format(form.adjusted_at, 'yyyy-MM-dd') : null,
      });
      if (data?.success) {
        toast.success(t('inventory.adjustmentSaved'));
        navigate("/inventory/stock-adjustments");
      } else {
        toast.error(data?.message || t('common.somethingWentWrong'));
      }
    } catch (err) {
      toast.error(err?.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  const difference =
    warehouseStock !== null && form.new_stock !== ""
      ? Number(form.new_stock) - warehouseStock
      : null;

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('inventory.adjustments'), t('inventory.newAdjustment')]}
          onBack={() => navigate("/inventory/stock-adjustments")}
        />

        <div className="subheader-page-content scrollable">
          <Row className="g-3 mb-3">
            <Col md="6">
              <Label>
                {t('inventory.product')}: <span className="text-danger">*</span>
              </Label>
              <CustomSelect
                module="inventory/products"
                value={form.product_id ? { value: form.product_id, label: selectedProduct ? selectedProduct.name : String(form.product_id) } : null}
                onChange={selected => {
                  const value = selected ? selected.value : "";
                  setForm(prev => ({ ...prev, product_id: value }));
                  const product = products.find(p => String(p.id) === String(value));
                  setSelectedProduct(product ?? null);
                }}
                placeholder={t('inventory.searchProduct')}
                canCreate={false}
                createLink="/inventory/products/create"
              />
            </Col>

            <Col md="3">
              <Label>
                {t('warehouses.warehouse')}: <span className="text-danger">*</span>
              </Label>
              <WarehouseSelect
                value={form.warehouse_id}
                onChange={(value) => setForm(prev => ({ ...prev, warehouse_id: value }))}
              />
            </Col>

            <Col md="3">
              <Label>{t('inventory.adjustmentDate')} <span className="text-danger">*</span></Label>
              <DatePicker
                selected={form.adjusted_at}
                onChange={(d) => setForm(prev => ({ ...prev, adjusted_at: d }))}
                className="form-control"
                dateFormat={datePickerFormat}
              />
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col md="4">
              <Label>{t('warehouses.stockInWarehouse')}</Label>
              <Input value={warehouseStock !== null ? warehouseStock : "—"} disabled />
            </Col>

            <Col md="4">
              <Label>
                {t('inventory.newStock')}: <span className="text-danger">*</span>
              </Label>
              <Input
                type="number"
                name="new_stock"
                value={form.new_stock}
                onChange={onChange}
                min="0"
              />
            </Col>

            <Col md="4">
              <Label>{t('inventory.difference')}:</Label>
              <Input
                value={
                  difference === null
                    ? "—"
                    : difference > 0
                    ? `+${difference}`
                    : String(difference)
                }
                disabled
                className={
                  difference === null
                    ? ""
                    : difference > 0
                    ? "text-success"
                    : difference < 0
                    ? "text-danger"
                    : ""
                }
              />
            </Col>
          </Row>

          <FormGroup>
            <Label>
              {t('inventory.reason')}: <span className="text-danger">*</span>
            </Label>
            <Input
              type="select"
              name="reason"
              value={form.reason}
              onChange={onChange}
            >
              <option value="">{t('common.selectReason')}</option>
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {t(`inventory.${r.key}`)}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup>
            <Label>{t('common.notes')}:</Label>
            <Input
              type="textarea"
              name="notes"
              value={form.notes}
              onChange={onChange}
              rows="4"
              placeholder={t('inventory.optionalNotesPlaceholder')}
            />
          </FormGroup>
        </div>
      </div>

      <FixedSave onSave={submit} loading={saveLoading} disabled={saveLoading} />
    </SidebarLayout>
  );
}

export default CreateAdjustmentsPage;
